import React, { useState, useEffect } from 'react';
import { RefreshCw, Clock, Pause, Play } from 'lucide-react';
import { formatTimeAgo } from '../hooks/useAutoRefresh';

/**
 * Auto-refresh status indicator component
 * Shows last update time and allows toggling auto-refresh
 */
const AutoRefreshIndicator = ({
    lastUpdated,
    isAutoRefreshEnabled,
    onToggle,
    onRefresh,
    loading,
    intervalSeconds = 30
}) => {
    const [timeAgo, setTimeAgo] = useState(formatTimeAgo(lastUpdated));

    // Update the "time ago" display every second
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeAgo(formatTimeAgo(lastUpdated));
        }, 1000);

        return () => clearInterval(timer);
    }, [lastUpdated]);

    return (
        <div className="flex items-center gap-3 text-sm">
            {/* Last updated */}
            <div className="flex items-center gap-1.5 text-gray-500">
                <Clock size={14} />
                <span>Updated: {timeAgo}</span>
            </div>

            {/* Auto-refresh toggle */}
            <button
                onClick={onToggle}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all ${isAutoRefreshEnabled
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                title={isAutoRefreshEnabled ? `Auto-refresh every ${intervalSeconds}s` : 'Auto-refresh paused'}
            >
                {isAutoRefreshEnabled ? (
                    <>
                        <Play size={12} className="fill-current" />
                        <span className="hidden sm:inline">Auto</span>
                    </>
                ) : (
                    <>
                        <Pause size={12} />
                        <span className="hidden sm:inline">Paused</span>
                    </>
                )}
            </button>

            {/* Manual refresh button */}
            <button
                onClick={onRefresh}
                disabled={loading}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all disabled:opacity-50"
                title="Refresh now"
            >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Refresh</span>
            </button>
        </div>
    );
};

export default AutoRefreshIndicator;
